/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { DateTime } from 'luxon'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Badge, Box, Form as NXUIForm, Theme } from '@nexigendigital/nxui'
import { domainAPI, useToggleAutoRenewMutation, useToggleIdProtectionMutation } from 'api/domain'
import { Weight } from 'models/enums'
import { parseISO } from 'helpers/utils'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Overview() {
    const domainId = parseInt(location.pathname.split('/')[2], 10)
    const { data: domain } = domainAPI.endpoints.domain.useQueryState(domainId)
    const [toggleAutoRenew] = useToggleAutoRenewMutation()
    const [toggleIdProtection] = useToggleIdProtectionMutation()

    function daysRemainsUntilExpired() {
        if (!domain?.meta.expiry) {
            return null
        }

        const expiry = parseISO(domain.meta.expiry)
        const now = DateTime.now()
        const expiryDiff = expiry.diff(now, 'days').toObject().days

        if (typeof expiryDiff === 'number') {
            return (`(${Math.round(expiryDiff)} days)`)
        }

        return null
    }

    return (
        <OverviewWrapper
            header={{
                element: (
                    <Header>
                        <Service>
                            <Name>
                                {domain?.domain}
                            </Name>
                        </Service>
                        <Badge color={'confirm'}>
                            {domain?.status.toUpperCase()}
                        </Badge>
                    </Header>
                )
            }}
        >
            <Info>
                <Column>
                    <span>
                        Registration Date
                    </span>
                    <span>
                        {domain?.created_at ? parseISO(domain.created_at).setLocale('en-AU').toLocaleString() : null}
                    </span>
                </Column>
                <Column>
                    <span>
                        Expiration Date
                    </span>
                    <span>
                        {domain?.meta.expiry ? parseISO(domain.meta.expiry).setLocale('en-AU').toLocaleString() : null}
                        {' '}
                        {daysRemainsUntilExpired()}
                    </span>
                </Column>
                <Column>
                    <span>Registrant Email Address</span>
                </Column>
                <Column>
                    <span>Auto-Renew</span>
                    <NXUIForm.Switch
                        checked={!!domain?.meta.auto_renew}
                        onClick={() => {
                            if (domain) {
                                toggleAutoRenew({
                                    id: domain.id,
                                    domain: {
                                        ...domain,
                                        meta: {
                                            ...domain?.meta,
                                            auto_renew: !domain?.meta.auto_renew
                                        }
                                    }
                                })
                            }
                        }}
                    />
                </Column>
                <Column>
                    <span>ID Protection</span>
                    <NXUIForm.Switch
                        checked={!!domain?.meta.id_protection}
                        onClick={() => {
                            if (domain) {
                                toggleIdProtection({
                                    id: domain?.id,
                                    domain: {
                                        ...domain,
                                        meta: {
                                            ...domain?.meta,
                                            id_protection: !domain?.meta.id_protection
                                        }
                                    }
                                })
                            }
                        }}
                    />
                </Column>
            </Info>
        </OverviewWrapper>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const OverviewWrapper = styled(Box)`
    margin-bottom: 16px;
    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-xl']))} {
        margin-top: 16px;
    }

    header {
        width: 100%;

        > div {
            width: 100%;
        }
    }

    footer {
        padding: 12px 24px 12px 0;
        display: flex;
        justify-content: flex-end;
    }
`

const Header = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
        flex-direction: column;
        align-items: flex-start;
        gap: 7px;
    }
`

const Service = styled.div`
    display: flex;
    gap: 5px;
`

const Name = styled.span`
    font-size: 18px;
    ${Theme.Functions.setFont('Inter', Weight.Medium)};
`

const Info = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-around;
    border-top: 1px solid ${props => props.theme['gray--100']};
    border-bottom: 1px solid ${props => props.theme['gray--100']};
    padding: 20px 0;
    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
        flex-direction: column;
        gap: 20px;
        padding: 20px;
    }
`

const Column = styled.div`
    font-size: .875rem;
    display: flex;
    flex-direction: column;
    gap: 10px;

    > span {
        ${Theme.Functions.setFont('Inter', Weight.Medium)};

        &:first-child {
            color: ${props => props.theme['text--100']};
        }
    }
`
